import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Wrench } from "lucide-react";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ExclusiveOffersPopup } from "@/components/ExclusiveOffersPopup";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { getPublicSettings } from "@/lib/api";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Calculator from "@/pages/Calculator";
import FinancialTools from "@/pages/FinancialTools";
import Leaderboard from "@/pages/Leaderboard";
import Knowledge from "@/pages/Knowledge";
import ReferInfo from "@/pages/ReferInfo";
import ReferLanding from "@/pages/ReferLanding";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import ApplyCampaign from "@/pages/ApplyCampaign";
import BulkWork from "@/pages/BulkWork";
import GetApp from "@/pages/GetApp";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";
import Reset from "@/pages/auth/Reset";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import AdminConsole from "@/pages/admin/AdminConsole";

function PublicLayout() {
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
      <ExclusiveOffersPopup />
    </>
  );
}

function MaintenancePage() {
  return (
    <div className="min-h-screen bg-[#0B223D] text-white flex items-center justify-center px-6" data-testid="maintenance-page">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl bg-teal-500/15 text-teal-400 flex items-center justify-center mx-auto mb-6">
          <Wrench className="w-8 h-8" />
        </div>
        <h1 className="font-display text-4xl font-extrabold">Under Maintenance</h1>
        <p className="text-white/70 mt-4 leading-relaxed">
          Hum website ko behtar bana rahe hain. Thodi der me wapas aa rahi hai — please check back soon.
        </p>
        <p className="text-white/50 text-sm mt-6">
          Urgent help: <a href="https://wa.me/919823567811" className="text-teal-400 font-semibold">WhatsApp +91 98235 67811</a>
        </p>
      </div>
    </div>
  );
}

function MaintenanceGate({ children }) {
  const [maint, setMaint] = React.useState(false);
  const loc = useLocation();
  React.useEffect(() => {
    getPublicSettings()
      .then((d) => {
        setMaint(!!d.maintenance_mode);
        if (d.google_site_verification) {
          let el = document.querySelector('meta[name="google-site-verification"]');
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute("name", "google-site-verification");
            document.head.appendChild(el);
          }
          el.setAttribute("content", d.google_site_verification);
        }
        if (d.google_analytics_id && !window.__ga_loaded) {
          window.__ga_loaded = true;
          const s1 = document.createElement("script");
          s1.async = true;
          s1.src = `https://www.googletagmanager.com/gtag/js?id=${d.google_analytics_id}`;
          document.head.appendChild(s1);
          const s2 = document.createElement("script");
          s2.innerHTML = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${d.google_analytics_id}');`;
          document.head.appendChild(s2);
        }
      })
      .catch(() => {});
  }, [loc.pathname]);
  const open = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/dashboard") || loc.pathname.startsWith("/apply") || ["/login", "/forgot-password", "/reset-password"].includes(loc.pathname);
  if (maint && !open) return <MaintenancePage />;
  return children;
}

function ManifestSwitcher() {
  const loc = useLocation();
  React.useEffect(() => {
    const role = new URLSearchParams(loc.search).get("role");
    let mf = "/manifest.json", title = "Credit Kendra";
    if (loc.pathname.startsWith("/admin") || (loc.pathname === "/login" && role === "super")) {
      mf = "/manifest-admin.json"; title = "Credit Kendra Admin";
    } else if (loc.pathname.startsWith("/dashboard") || (loc.pathname === "/login" && role === "adviser")) {
      mf = "/manifest-adviser.json"; title = "Credit Kendra Adviser";
    } else if (loc.pathname === "/login") {
      mf = "/manifest-team.json"; title = "Credit Kendra Team";
    }
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", mf);
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement("meta");
      appleTitle.setAttribute("name", "apple-mobile-web-app-title");
      document.head.appendChild(appleTitle);
    }
    appleTitle.setAttribute("content", title);
  }, [loc.pathname, loc.search]);
  return null;
}


function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <ManifestSwitcher />
          <MaintenanceGate>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:pid" element={<ProductDetail />} />
              <Route path="/calculator" element={<Calculator />} />
              <Route path="/tools" element={<FinancialTools />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/knowledge" element={<Knowledge />} />
              <Route path="/refer" element={<ReferInfo />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/bulk-work" element={<BulkWork />} />
              <Route path="/get-app" element={<GetApp />} />
              <Route path="/r/:linkId" element={<ReferLanding />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/apply/:code" element={<ApplyCampaign />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<Forgot />} />
            <Route path="/reset-password" element={<Reset />} />
            <Route path="/dashboard/*" element={
              <ProtectedRoute roles={["user"]}><DashboardLayout /></ProtectedRoute>
            } />
            <Route path="/admin/*" element={
              <ProtectedRoute roles={["admin", "manager", "staff"]}><AdminConsole /></ProtectedRoute>
            } />
          </Routes>
          </MaintenanceGate>
          <WhatsAppFab />
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
